import React from "react"
import Layout from "../components/layout"
import SectionContent from "../components/section_content"
import { graphql } from "gatsby"
import { getSrc } from "gatsby-plugin-image"
import { SEO } from "../components/seo"

const getTranslations = props => {
  if (props.pageContext.textStyleId) {
    const { translations } = props.data.textStyle
    return translations.map(translation => {
      return {
        link: `/${translation.path}/`,
        code: translation.language.code,
        nativeName: translation.language.nativeName,
      }
    })
  } else {
    const { languages } = props.data
    return languages.nodes.map(language => {
      return {
        link: language.code !== "en" ? `/${language.code}/` : `/`,
        code: language.code,
        nativeName: language.nativeName,
      }
    })
  }
}

class SectionPage extends React.PureComponent {
  getBreadcrumbs() {
    const language_code = this.props.data.language.code
    const style_path = this.props.data.stylePath.string
    const breadcrumbs = [
      {
        text: this.props.data.homeHeader.string,
        link: language_code === "en" ? `/` : `/${language_code}/`,
      },
    ]
    if (this.props.pageContext.textStyleId) {
      breadcrumbs.push({
        text: this.props.data.styleHeader.string,
        link:
          language_code === "en"
            ? `/${style_path}/`
            : `/${language_code}/${style_path}/`,
      })
      breadcrumbs.push({
        text: this.props.data.textStyle.shortTitle,
        link: `/${this.props.data.textStyle.path}/`,
      })
    }
    return breadcrumbs
  }

  getPageTitle() {
    const { pageContext } = this.props
    let page_title = `${this.props.data.sharedH2Title.string} `
    page_title += pageContext.textStyleId
      ? this.props.data.textStyle.description
      : `${this.props.data.indexH2Title.string}`
    return page_title
  }

  render() {
    const { pageContext, data } = this.props
    const page_image =
      pageContext.textStyleId && data.textStyle.mainImage
        ? data.textStyle.mainImage.image
        : null
    return (
      <Layout
        layoutContext={pageContext.layoutContext}
        pageTitle={this.getPageTitle()}
        breadcrumbs={this.getBreadcrumbs()}
        translatedLinks={getTranslations(this.props)}
        moreInfo={
          pageContext.textStyleId ? this.props.data.textStyle.moreInfo : null
        }
        moreInfoLinks={
          pageContext.textStyleId
            ? this.props.data.textStyle.moreInfoLinks
            : null
        }
        pageImage={page_image}
        content={
          <SectionContent
            {...this.props}
            hello_text_string={
              pageContext.textStyleId
                ? this.props.data.textStyle.helloTextString
                : null
            }
          />
        }
      ></Layout>
    )
  }
}

export default SectionPage

export const Head = props => {
  const { data, pageContext } = props
  let head_title = pageContext.textStyleId
    ? data.textStyle.title
    : data.indexTitle.string
  let meta_description = pageContext.textStyleId
    ? data.textStyle.description
    : `${data.indexMetaDescription.string}`
  let page_image =
    pageContext.textStyleId && data.textStyle.mainImage
      ? data.textStyle.mainImage.image
      : null
  return (
    <SEO {...props} translatedLinks={getTranslations(props)}>
      <title>{head_title}</title>
      <meta name="description" content={meta_description} />
      <meta property="og:title" content={head_title} />
      <meta property="og:description" content={meta_description} />
      {page_image && (
        <meta
          property="og:image"
          content={`https://yaytext.com${getSrc(page_image)}`}
        />
      )}
    </SEO>
  )
}

export const pageQuery = graphql`
  query StylePageQuery($languageId: String, $textStyleId: String) {
    language: yaytextLanguage(languageId: { eq: $languageId }) {
      code
    }
    languages: allYaytextLanguage {
      nodes {
        code
        nativeName
      }
    }
    textStyle: yaytextTextStyle(
      textStyleId: { eq: $textStyleId }
      language: { languageId: { eq: $languageId } }
    ) {
      shortTitle
      title
      description
      relatedDescription
      relatedCtd
      moreInfo
      moreInfoLinks
      moreInfoCtd
      path
      hasPreviewImages
      helloTextString
      translations {
        path
        language {
          code
          nativeName
        }
      }
      mainImage {
        image {
          childImageSharp {
            gatsbyImageData(layout: CONSTRAINED, width: 400)
          }
        }
      }
      relatedTextTransforms {
        label
        slug
        previewFont
        actions
        hasPreviewImages
        textStyle {
          path
        }
      }
    }
    textTransforms: allYaytextTextTransform(
      filter: {
        textStyle: {
          textStyleId: { eq: $textStyleId }
          language: { languageId: { eq: $languageId } }
        }
      }
      sort: { sectionWeight: ASC }
    ) {
      nodes {
        label
        slug
        previewFont
        actions
        hasPreviewImages
        textStyle {
          path
        }
        previewImages {
          os
          app
          site
          file {
            image {
              childImageSharp {
                gatsbyImageData(layout: CONSTRAINED, width: 431)
              }
            }
          }
        }
      }
    }
    homeHeader: yaytextGlobalString(
      key: { eq: "home-header" }
      language: { languageId: { eq: $languageId } }
    ) {
      string
    }
    stylePath: yaytextGlobalString(
      key: { eq: "styles-slug" }
      language: { languageId: { eq: $languageId } }
    ) {
      string
    }
    styleHeader: yaytextGlobalString(
      key: { eq: "styles-header" }
      language: { languageId: { eq: $languageId } }
    ) {
      string
    }
    sharedH2Title: yaytextGlobalString(
      key: { eq: "shared_h2_title" }
      language: { languageId: { eq: $languageId } }
    ) {
      string
    }
    indexTitle: yaytextGlobalString(
      key: { eq: "index_title" }
      language: { languageId: { eq: $languageId } }
    ) {
      string
    }
    indexH2Title: yaytextGlobalString(
      key: { eq: "index_h2_title" }
      language: { languageId: { eq: $languageId } }
    ) {
      string
    }
    indexMetaDescription: yaytextGlobalString(
      key: { eq: "index_meta_description" }
      language: { languageId: { eq: $languageId } }
    ) {
      string
    }
    helloTextString: yaytextGlobalString(
      key: { eq: "hello_text_string" }
      language: { languageId: { eq: $languageId } }
    ) {
      string
    }
    yourText: yaytextGlobalString(
      key: { eq: "your_text" }
      language: { languageId: { eq: $languageId } }
    ) {
      string
    }
    notifyCopied: yaytextGlobalString(
      key: { eq: "notify-copied" }
      language: { languageId: { eq: $languageId } }
    ) {
      string
    }
    copiedExclamation: yaytextGlobalString(
      key: { eq: "copied_exclamation" }
      language: { languageId: { eq: $languageId } }
    ) {
      string
    }
    copy: yaytextGlobalString(
      key: { eq: "copy" }
      language: { languageId: { eq: $languageId } }
    ) {
      string
    }
    tweet: yaytextGlobalString(
      key: { eq: "tweet" }
      language: { languageId: { eq: $languageId } }
    ) {
      string
    }
    preview: yaytextGlobalString(
      key: { eq: "preview" }
      language: { languageId: { eq: $languageId } }
    ) {
      string
    }
  }
`
